import { useState, useMemo, useEffect, useRef } from "react";
import { VIZ_ROOMS, BATHROOM_PRODUCTS, KITCHEN_JOINERY, roomImage, bathroomImage, bathroomColourImage } from "@/data/visualizer";
import { BATH_COLOUR_RENDERED } from "./shared";
import RoomShareDialog from "./RoomShareDialog";
import { Mail } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { useStudio } from "@/context/StudioContext";
import PreviewFrame from "./PreviewFrame";
import ProductAccordion from "./ProductAccordion";
import BathroomPicker from "./BathroomPicker";
import { RENDERED, AI_CACHE_MAX, BACKEND, trimCache, urlToB64, b64ToBlobUrl } from "./shared";
import { trackVisualiserChange } from "@/lib/analytics";

/**
 * Curated Rooms mode: pick a room + product + colour, see it rendered.
 * Non-base colours trigger an AI re-tint via /api/visualise/apply. Bathroom
 * dual-material combos support colour swap for floor AND wall independently.
 */
export default function CuratedRooms() {
  const [roomKey, setRoomKey] = useState(VIZ_ROOMS[0].key);
  const [productId, setProductId] = useState(PRODUCTS[0].id);
  const [colourIdx, setColourIdx] = useState(0);
  const [openElement, setOpenElement] = useState("Timber");
  const [bathFloor, setBathFloor] = useState(BATHROOM_PRODUCTS[0].id);
  const [bathWall, setBathWall] = useState(BATHROOM_PRODUCTS[1].id);
  const [bathFloorColourIdx, setBathFloorColourIdx] = useState(0);
  const [bathWallColourIdx, setBathWallColourIdx] = useState(0);
  const [joineryIdx, setJoineryIdx] = useState(0); // Kitchen-only cabinet finish
  const [colourRenders, setColourRenders] = useState({});
  const [colourLoading, setColourLoading] = useState(false);
  const [colourError, setColourError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const { openBooking, openSample } = useStudio();

  const room = VIZ_ROOMS.find((r) => r.key === roomKey) || VIZ_ROOMS[0];

  const eligibleProducts = useMemo(
    () => PRODUCTS.filter((p) => room.allowedDisciplines.includes(p.discipline)),
    [room],
  );

  // Snap product back to something eligible when the room changes.
  useEffect(() => {
    if (!room.dualMaterial && !eligibleProducts.some((p) => p.id === productId)) {
      setProductId(eligibleProducts[0]?.id || PRODUCTS[0].id);
    }
  }, [room, eligibleProducts, productId]);

  const product = PRODUCTS.find((p) => p.id === productId) || PRODUCTS[0];
  const colours = product.colours || [];
  const colour = colours[colourIdx] || colours[0];
  const floorProduct = PRODUCTS.find((p) => p.id === bathFloor);
  const wallProduct = PRODUCTS.find((p) => p.id === bathWall);
  const floorColour = floorProduct?.colours?.[bathFloorColourIdx];
  const wallColour = wallProduct?.colours?.[bathWallColourIdx];

  // Reset single-material colour whenever product/room changes.
  useEffect(() => { setColourIdx(0); setColourError(""); }, [productId, roomKey]);
  // Reset joinery when leaving the Kitchen (or switching products).
  useEffect(() => { if (roomKey !== "kitchen") setJoineryIdx(0); }, [roomKey]);
  useEffect(() => { setJoineryIdx(0); }, [productId]);
  // Reset bathroom colours whenever the floor/wall product itself changes.
  useEffect(() => { setBathFloorColourIdx(0); }, [bathFloor]);
  useEffect(() => { setBathWallColourIdx(0); }, [bathWall]);
  // Clear any stale error when entering/leaving bathroom mode.
  useEffect(() => { setColourError(""); }, [room.dualMaterial]);

  // Analytics — fire a debounced visualiser_used event whenever the
  // meaningful state changes. Debounced by 400ms so rapid clicks collapse
  // into a single event.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const t = window.setTimeout(() => {
      trackVisualiserChange({
        room: roomKey,
        productId,
        colourIdx,
        joinery: roomKey === "kitchen" ? KITCHEN_JOINERY[joineryIdx]?.id : null,
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [roomKey, productId, colourIdx, joineryIdx]);

  // Pre-generated colour variant paths look like:
  //   /visualizer/<productId>-c<idx>-<roomKey>.jpg
  // If the file exists (all colours pre-rendered), we use it directly — no
  // AI call, no credit burn. If it doesn't exist yet, we fall back to the
  // legacy runtime AI swap.
  const staticColourSrc = colourIdx > 0 && RENDERED.has(product.id) && !room.dualMaterial
    ? `/visualizer/${product.id}-c${colourIdx}-${room.key}.jpg`
    : null;

  // Kitchen joinery override — pre-rendered <pid>-c<idx>-kitchen-<joineryId>.jpg
  // for non-base colours (falls back via handleImgError to the base-colour
  // joinery variant if the specific combo isn't pre-generated).
  const joinery = KITCHEN_JOINERY[joineryIdx];
  const staticJoinerySrc = room.key === "kitchen"
    && joineryIdx > 0
    && RENDERED.has(product.id)
    ? (colourIdx === 0
        ? `/visualizer/${product.id}-kitchen-${joinery.id}.jpg`
        : `/visualizer/${product.id}-c${colourIdx}-kitchen-${joinery.id}.jpg`)
    : null;

  const baseRoomSrc = RENDERED.has(product.id)
    ? roomImage(product.id, room.key)
    : (colour?.image || product.swatch);

  const renderKey = `${product.id}::${room.key}::${colourIdx}`;
  const cachedRender = colourRenders[renderKey];

  // Bathroom combo base image (used both for the base render and as the
  // canvas the AI recolours). Combo image may not exist for every pair —
  // callers should also register onError fallback below.
  const bathBaseSrc = bathroomImage(bathFloor, bathWall);
  const bathRenderKey = `bath::${bathFloor}:${bathFloorColourIdx}::${bathWall}:${bathWallColourIdx}`;
  const bathCached = colourRenders[bathRenderKey];
  const bathHasNonBase = bathFloorColourIdx > 0 || bathWallColourIdx > 0;
  // Pre-rendered colour combination — instant, no model call, no credits.
  const bathStaticColourSrc = bathHasNonBase
    && BATH_COLOUR_RENDERED.has(`${bathFloor}-c${bathFloorColourIdx}-wall-${bathWall}-c${bathWallColourIdx}`)
    ? bathroomColourImage(bathFloor, bathFloorColourIdx, bathWall, bathWallColourIdx)
    : null;

  const previewSrc = room.dualMaterial
    ? (bathHasNonBase ? (bathStaticColourSrc || bathCached || bathBaseSrc) : bathBaseSrc)
    : (staticJoinerySrc  // kitchen joinery override (zero-credit)
        || (colourIdx === 0
              ? baseRoomSrc
              : (staticColourSrc
                  || cachedRender
                  || (RENDERED.has(product.id) ? baseRoomSrc : (colour?.image || product.swatch)))));

  const previewLabel = room.dualMaterial
    ? `${floorProduct?.name} floor · ${wallProduct?.name} wall`
    : product.name;
  const previewSub = room.dualMaterial
    ? `Bathroom · ${floorProduct?.discipline} floor${floorColour && bathFloorColourIdx > 0 ? ` (${floorColour.name})` : ""} + ${wallProduct?.discipline} wall${wallColour && bathWallColourIdx > 0 ? ` (${wallColour.name})` : ""}`
    : (RENDERED.has(product.id)
        ? `${product.finish}${colourIdx > 0 && colour ? ` · ${colour.name}` : ""}`
        : `${product.finish} · swatch preview${colourIdx > 0 && colour ? ` · ${colour.name}` : ""}`);

  // --- Single-material AI colour swap ---------------------------------
  const revokeBlob = (_k, v) => { if (v && v.startsWith("blob:")) URL.revokeObjectURL(v); };
  // Revoke all cached blob URLs when the component unmounts.
  useEffect(() => () => {
    Object.values(colourRenders).forEach((v) => revokeBlob(null, v));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Single-material colour swap: NO-OP ------------------------------
  // Every non-base single-material colour variant is pre-generated as a
  // static JPEG under /public/visualizer/<pid>-c<idx>-<room>.jpg (see
  // /app/scripts/gen_colour_variants.py). previewSrc points directly at
  // the static file — zero runtime AI credits burned. This effect is kept
  // only to reset colourError when the room mode changes.
  useEffect(() => { setColourError(""); }, [productId, roomKey, colourIdx]);

  // --- Dual-material (bathroom) AI colour swap ------------------------
  useEffect(() => {
    if (!room.dualMaterial) return;
    if (!bathHasNonBase) return;
    if (bathStaticColourSrc) return;      // already pre-rendered on disk
    if (colourRenders[bathRenderKey]) return;
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      setColourLoading(true); setColourError("");
      try {
        // Try the combo image first; fall back to the floor's solo bathroom
        // render if the specific combo doesn't exist.
        let b64;
        try {
          b64 = await urlToB64(bathBaseSrc);
        } catch {
          b64 = await urlToB64(roomImage(bathFloor, "bathroom"));
        }
        const parts = [];
        if (bathFloorColourIdx > 0 && floorColour) {
          parts.push(`Recolour the FLOOR (${floorProduct.name} — ${floorProduct.finish}) to a ${floorColour.name} tone (approx ${floorColour.hex}).`);
        }
        if (bathWallColourIdx > 0 && wallColour) {
          parts.push(`Recolour the WALL cladding (${wallProduct.name} — ${wallProduct.finish}) to a ${wallColour.name} tone (approx ${wallColour.hex}).`);
        }
        const desc = `Keep the exact bathroom composition, lighting, tapware, joinery and camera angle. ${parts.join(" ")} Preserve the tile/stone joint pattern, scale and material texture — shift only the colour and tonality of the specified surfaces.`;
        const r = await fetch(`${BACKEND}/api/visualise/apply`, {
          method: "POST",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_b64: b64, product_description: desc }),
        });
        if (!r.ok) {
          let detail = "";
          try { detail = (await r.json())?.detail || ""; } catch (e) { console.warn("[visualise] non-JSON error body:", e); }
          throw new Error(detail || `HTTP ${r.status}`);
        }
        const j = await r.json();
        if (cancelled) return;
        const url = b64ToBlobUrl(j.mime_type, j.image_b64);
        setColourRenders((prev) => trimCache({ ...prev, [bathRenderKey]: url }, AI_CACHE_MAX, revokeBlob));
      } catch (e) {
        if (cancelled || e?.name === "AbortError") return;
        // Never surface a backend/limit error to a visitor: the base pair render
        // is already on screen, so just note that the tone shown is the base one.
        console.warn("[visualise] colour render unavailable:", e?.message);
        setColourError(
          "Showing this pairing in its base tones — ask the studio for a bespoke render of this exact combination."
        );
      } finally {
        if (!cancelled) setColourLoading(false);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [room.dualMaterial, bathFloor, bathWall, bathFloorColourIdx, bathWallColourIdx, bathRenderKey, bathHasNonBase, bathStaticColourSrc]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImgError = (e) => {
    if (room.dualMaterial) {
      if (!e.currentTarget.src.endsWith(bathBaseSrc)) { e.currentTarget.src = bathBaseSrc; return; }
      const fb = roomImage(bathFloor, "bathroom");
      if (!e.currentTarget.src.endsWith(fb)) e.currentTarget.src = fb;
      return;
    }
    // Joinery combo missing → try the base-colour joinery variant, then the
    // coloured base kitchen, then the base kitchen, then the swatch.
    const cur = e.currentTarget.src;
    const jn = joinery?.id;
    if (jn && room.key === "kitchen" && joineryIdx > 0) {
      const baseJoinery = `/visualizer/${product.id}-kitchen-${jn}.jpg`;
      if (!cur.endsWith(baseJoinery)) { e.currentTarget.src = baseJoinery; return; }
    }
    if (colourIdx > 0) {
      const cRoom = `/visualizer/${product.id}-c${colourIdx}-${room.key}.jpg`;
      if (!cur.endsWith(cRoom)) { e.currentTarget.src = cRoom; return; }
    }
    const baseRoom = `/visualizer/${product.id}-${room.key}.jpg`;
    if (!cur.endsWith(baseRoom)) { e.currentTarget.src = baseRoom; return; }
    if (!cur.endsWith(product.swatch)) e.currentTarget.src = product.swatch;
  };

  const activeColourName = room.dualMaterial
    ? (bathFloorColourIdx > 0 ? floorColour?.name : wallColour?.name) || "colour"
    : colour?.name || "colour";

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      {/* PREVIEW */}
      <div className="lg:col-span-8 order-1">
        <PreviewFrame
          src={previewSrc}
          alt={`${previewLabel} in a ${room.label.toLowerCase()}`}
          imgKey={previewSrc}
          onError={handleImgError}
          loadingLabel={colourLoading ? `Rendering ${activeColourName}…` : null}
          loadingSwatch={colourLoading ? (
            room.dualMaterial
              ? {
                  image: bathFloorColourIdx > 0 ? floorColour?.image : wallColour?.image,
                  name:  bathFloorColourIdx > 0 ? `${floorProduct?.name} · ${floorColour?.name}` : `${wallProduct?.name} · ${wallColour?.name}`,
                  hex:   bathFloorColourIdx > 0 ? floorColour?.hex   : wallColour?.hex,
                }
              : {
                  image: colour?.image,
                  name:  `${product.name} · ${colour?.name}`,
                  hex:   colour?.hex,
                }
          ) : null}
          caption={{
            eyebrow: room.label,
            title: previewLabel,
            sub: previewSub,
            error: colourError,
          }}
        />
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          data-testid="viz-share-btn"
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#8C7355]/50 px-6 py-3 text-[10px] tracking-[0.25em] uppercase text-[#C9A876] hover:border-[#C9A876] hover:text-[#dcb884]"
          style={{ transition: "color .4s ease, border-color .4s ease" }}
        >
          <Mail size={13} strokeWidth={1.8} />
          Email me this room
        </button>
        <RoomShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          room={room.label}
          spec={`${previewLabel}${previewSub ? ` — ${previewSub}` : ""}`}
          imageUrl={typeof previewSrc === "string" && previewSrc.startsWith("/") ? previewSrc : null}
        />
      </div>

      {/* CONTROLS */}
      <aside className="lg:col-span-4 order-2 space-y-4">
        <label className="block">
          <span className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase mb-2 block">Room</span>
          <select
            data-testid="viz-room-select"
            value={roomKey}
            onChange={(e) => setRoomKey(e.target.value)}
            className="w-full bg-[#1a1b1e] border border-[#8C7355]/25 text-[#F5F4F0] px-4 py-3 text-sm font-body focus:outline-none focus:border-[#C9A876]"
          >
            {VIZ_ROOMS.map((r) => (
              <option key={r.key} value={r.key}>{r.label}{r.dualMaterial ? " · Floor + Wall" : ""}</option>
            ))}
          </select>
          <span className="text-[#D6D1CA]/40 text-[9px] tracking-[0.28em] uppercase mt-2 block font-body">
            Allowed: {room.allowedDisciplines.join(" · ")}
          </span>
        </label>

        {room.key === "kitchen" && (
          <div data-testid="viz-joinery">
            <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase mb-2">
              Joinery <span className="text-[#D6D1CA]/40 ml-2">Polytec</span>
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {KITCHEN_JOINERY.map((j, i) => {
                const active = i === joineryIdx;
                return (
                  <button
                    key={j.name}
                    data-testid={`viz-joinery-${j.id || "default"}`}
                    onClick={() => setJoineryIdx(i)}
                    title={j.name}
                    aria-label={j.name}
                    className="relative w-7 h-7 rounded-full transition-transform duration-200 hover:scale-110"
                    style={{
                      background: j.hex,
                      boxShadow: active
                        ? "0 0 0 1.5px #121315, 0 0 0 3px #C9A876"
                        : "inset 0 0 0 1px rgba(214,209,202,0.35)",
                    }}
                  />
                );
              })}
            </div>
            <span className="text-[#D6D1CA]/60 text-[10px] tracking-[0.28em] uppercase mt-2 block font-body">
              {joinery.name}
            </span>
          </div>
        )}

        {room.dualMaterial ? (
          <>
            <BathroomPicker
              label="Floor"
              testid="viz-floor-products"
              value={bathFloor}
              onChange={setBathFloor}
              colourIdx={bathFloorColourIdx}
              setColourIdx={setBathFloorColourIdx}
            />
            <BathroomPicker
              label="Wall"
              testid="viz-wall-products"
              value={bathWall}
              onChange={setBathWall}
              colourIdx={bathWallColourIdx}
              setColourIdx={setBathWallColourIdx}
            />
          </>
        ) : (
          <ProductAccordion
            eligibleProducts={eligibleProducts}
            productId={productId}
            setProductId={setProductId}
            colourIdx={colourIdx}
            setColourIdx={setColourIdx}
            openElement={openElement}
            setOpenElement={setOpenElement}
          />
        )}

        <div className="flex flex-col gap-2 pt-4 border-t border-[#8C7355]/12">
          <button
            data-testid="viz-request-sample"
            onClick={openSample}
            className="btn-bronze rounded-full px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-medium"
          >
            Request a Sample
          </button>
          <button
            data-testid="viz-request-appointment"
            onClick={openBooking}
            className="rounded-full border border-[#8C7355]/40 px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-medium text-[#F5F4F0] hover:bg-[#8C7355]/15"
          >
            Request Appointment
          </button>
        </div>
      </aside>
    </div>
  );
}
